import React, { useEffect, useRef, useState } from "react";
import { Tag } from "@wwg/ui";
import Button from "shared/Button";
import styles from "./index.module.css";
import classNames from "classnames";
import { Thumbnail } from "../../../CMS/components/Thumbnail";
import moment from "moment";
import AudioIcon from "../../../CMS/icons/Audio_Default_Icon.svg";
import KeynoteIcon from "../../../CMS/icons/Keynote_Default_Icon.svg";
import LinkIcon from "../../../CMS/icons/Link_Default_Icon.svg";
import PDFIcon from "../../../CMS/icons/PDF_Default_Icon.svg";
import PPTIcon from "../../../CMS/icons/Powerpoint_Default_Icon.svg";
// import ThumbnailIcon from "../../../CMS/icons/Thumbnail_Icon.svg";
import VideoIcon from "../../../CMS/icons/Video_Default_Icon.svg";
import DocIcon from "../../icons/Doc_Default_Icon.svg";
import { Auth } from "shared/@auth";
import { FILE_TYPE } from "Marketing/CMS/enums";
import { ContentSummary } from "Marketing/CMS/components/ContentSummary";

const fileIcons = {
  [FILE_TYPE.pdf]: {
    label: "PDF",
    icon: PDFIcon,
  },
  [FILE_TYPE.audio]: {
    label: "Audio",
    icon: AudioIcon,
  },
  [FILE_TYPE.video]: {
    label: "Video",
    icon: VideoIcon,
  },
  [FILE_TYPE.document]: {
    label: "DOC",
    icon: DocIcon,
  },
  [FILE_TYPE.ppt]: {
    label: "PPT",
    icon: PPTIcon,
  },
  [FILE_TYPE.pps]: {
    label: "PPS",
    icon: PPTIcon,
  },
  [FILE_TYPE.keynote]: {
    label: "Keynote",
    icon: KeynoteIcon,
  },
  [FILE_TYPE.url]: {
    label: "URL",
    icon: LinkIcon,
  },
};

export function ContentRow({
  hasPermission,
  item,
  onEditClicked,
  selectedFilters,
  setSelectedFilters,
  onArchiveClicked,
}) {
  const {
    title,
    subTitle,
    description,
    country,
    publishOn,
    thumbnail,
    tags,
    fileFormat,
    status,
  } = item;

  const tagRowRef = useRef(null);
  const tagMeasureRef = useRef(null);
  const visibleTags = tags.filter(
    (tag) => tag.label !== "New!" && !tag.hidden && !tag.internal
  );
  const [visibleTagCount, setVisibleTagCount] = useState(visibleTags.length);
  const [showTagOverflowDots, setShowTagOverflowDots] = useState(false);

  useEffect(() => {
    const updateOverflowState = () => {
      if (!tagRowRef.current || !tagMeasureRef.current || !visibleTags.length) {
        setVisibleTagCount(visibleTags.length);
        setShowTagOverflowDots(false);
        return;
      }

      const rowWidth = tagRowRef.current.clientWidth;
      const tagWidths = Array.from(tagMeasureRef.current.children).map(
        (node) => node.offsetWidth
      );
      const gap = 6;
      const overflowDotsWidth = 25 + gap;
      let used = 0;
      let visible = 0;

      for (const tagWidth of tagWidths) {
        const nextWidth = (visible > 0 ? gap : 0) + tagWidth;
        if (used + nextWidth > rowWidth) break;
        used += nextWidth;
        visible += 1;
      }

      const hasOverflow = visible < visibleTags.length;
      while (
        hasOverflow &&
        visible > 1 &&
        used + overflowDotsWidth > rowWidth
      ) {
        used -= tagWidths[visible - 1] + gap;
        visible -= 1;
      }

      setVisibleTagCount(Math.max(visible, hasOverflow ? 1 : 0));
      setShowTagOverflowDots(hasOverflow);
    };

    updateOverflowState();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateOverflowState);
      if (tagRowRef.current) observer.observe(tagRowRef.current);
    } else {
      window.addEventListener("resize", updateOverflowState);
    }

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", updateOverflowState);
    };
  }, [tags, visibleTags.length]);

  const handleTagClick = (tag) => {
    const filterId = `tags${tag.id}`;
    if (selectedFilters.some((filter) => filter.filterId === filterId)) {
      setSelectedFilters(
        selectedFilters.filter((filter) => filter.filterId !== filterId)
      );
    } else {
      setSelectedFilters([
        ...selectedFilters,
        {
          id: tag.id,
          label: tag.label,
          filterId,
          title: "Keyword Tags",
          type: "tags",
        },
      ]);
    }
  };

  const renderTags = (items) =>
    items.map((tag, index) => (
      <div
        className={styles.tagItem}
        key={`${tag.id}-${index}`}
        onClick={() => handleTagClick(tag)}
      >
        <Tag>{tag.label}</Tag>
      </div>
    ));

  const icons = fileFormat.map((file) => {
    const { icon, label } = fileIcons[file] || {};
    return { icon: icon, label: label };
  });

  return (
    <>
      <div className={styles.container} data-cms-content-row>
        <div className={styles.col}>
          <div className={classNames(styles.row, styles.pad)}>
            <div className={classNames(styles.center, styles.mediaImg)}>
              <Thumbnail
                src={
                  thumbnail ||
                  (icons &&
                  Array.isArray(icons) &&
                  icons.length > 0 &&
                  icons[0] &&
                  icons[0].icon
                    ? icons[0].icon
                    : null)
                }
                alt={`${title}, thumbnail`}
              />
            </div>
            <div className={styles.details}>
              <div className={classNames(styles.col, styles.center)}>
                <div className={styles.title}>{title}</div>
                {subTitle && <div className={styles.smallText}>{subTitle}</div>}
                <div className={styles.smallText}>
                  {icons &&
                    icons.length > 0 &&
                    icons
                      .filter((icon) => JSON.stringify(icon) !== "{}")
                      .map((icon) => icon.label)
                      .join("/")}
                </div>
                {publishOn && (
                  <div className={styles.smallText}>
                    {moment(publishOn).format("M.D.YYYY")}
                  </div>
                )}
              </div>
              <div className={styles.summaryActions}>
                <ContentSummary item={item} />
                <div className={styles.actions}>
                  {hasPermission && (
                    <div className={classNames(styles.row, styles.button)}>
                      <Button
                        className={styles.btnArchive}
                        onClick={() => {
                          onArchiveClicked();
                        }}
                      >
                        <span>{status === 3 ? "Unarchive" : "Archive"}</span>
                      </Button>
                      <Button
                        className={styles.btnEdit}
                        onClick={() => {
                          onEditClicked();
                        }}
                      >
                        <span>Edit</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {visibleTags.length > 0 && (
                <div className={styles.tags} ref={tagRowRef}>
                  <div
                    className={styles.tagMeasureRow}
                    ref={tagMeasureRef}
                    aria-hidden="true"
                  >
                    {renderTags(visibleTags)}
                  </div>
                  <div className={styles.tagTrack}>
                    {renderTags(visibleTags.slice(0, visibleTagCount))}
                  </div>
                  {showTagOverflowDots && (
                    <span className={styles.overflowDots} aria-hidden="true">
                      <span className={styles.overflowDot} />
                      <span className={styles.overflowDot} />
                      <span className={styles.overflowDot} />
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

ContentRow.propTypes = {};
